/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import NProgress from 'nprogress';
import Backbone from 'backbone';

import Scaffold from '@/Scaffold';
import * as NavUtils from '@/util/NavUtils';
import { userHasRole, getUserDetails, isAuthorized } from '@/util/RoleUtils';
import ErrorView from '@/views/ErrorView';

import GetCustomerAssessmentById from '../services/GetCustomerAssessmentById';
import GetAllAssessmentStatuses from '../services/GetAllAssessmentStatuses';
import GetAllAssessmentTypes from '../services/GetAllAssessmentTypes';
import AssessmentPage from '../pages/AssessmentPage';
import GetAssessmentAttachments from '../services/GetAssessmentAttachments';
import GetAllExternalUsers from '../../common/services/GetAllExternalUsers';

export default async function ShowAssessmentPage(assessmentId) {
  NProgress.start();

  try {
    if (!isAuthorized() && EXTERNAL) {
      showAuthError();
      return;
    }
    assessmentId = parseInt(assessmentId);
    const assessment = await GetCustomerAssessmentById(assessmentId);

    if (!assessment?.id) {
      throw new Error('Not Found');
    }

    const isCRS = userHasRole('CRS');
    const isEPC = userHasRole('EPC');
    const isCRSOwner = getUserDetails().id === assessment?.crsOwner;
    const isInternalUser = isCRS || isEPC;

    let isReadOnly = true;
    let returnable = false;

    if (isCRS) {
      NavUtils.navigate(`assessment/${assessmentId}`);
      if (assessment.statusId === '3') {
        // CRS Review
        isReadOnly = true;
        returnable = true;
      }
    } else if (!isInternalUser) {
      NavUtils.navigate(`ext/assessment/${assessmentId}`);
      if (assessment.statusId === '1' || assessment.statusId === '4') {
        // Draft or Returned
        isReadOnly = false;
      }
    } else {
      throw new Error('Forbidden');
    }

    let assessmentStatuses;
    let assessmentTypes;
    let attachments;
    let externalUsers;
    let customerName;

    if (EXTERNAL) {
      [assessmentStatuses, assessmentTypes, attachments] = await Promise.all([
        GetAllAssessmentStatuses(),
        GetAllAssessmentTypes(),
        GetAssessmentAttachments(assessmentId)
      ]);
    } else {
      [assessmentStatuses, assessmentTypes, attachments, externalUsers] = await Promise.all([
        GetAllAssessmentStatuses(),
        GetAllAssessmentTypes(),
        GetAssessmentAttachments(assessmentId),
        GetAllExternalUsers()
      ]);
      const user = _.find(externalUsers, { otdsUserId: assessment.createdBy });
      customerName = user.firstName.concat(' ', user.lastName);
    }

    Scaffold.getRegion('content').show(
      new AssessmentPage({
        model: new Backbone.Model({
          assessmentId,
          commandName: assessment.commandName,
          assignedCRS: assessment.assignedCRS,
          assessmentStatus: isCRS ? assessment.internalAssessmentStatus : assessment.externalAssessmentStatus,
          assessmentStatusId: assessment.statusId,
          assessmentStatuses,
          assessmentType: assessment.type,
          assessmentTypeId: assessment.typeID,
          assessmentTypes,
          attachments,
          isReadOnly,
          isInternalUser,
          isCRSOwner,
          customerName,
          returnable,
          returnLink: '#ext/dashboard',
          returnLabel: 'Return to Dashboard'
        })
      })
    );
  } catch (err) {
    Scaffold.getRegion('content').show(
      new ErrorView({
        model: new Backbone.Model({
          errorMessage: _.isString(err.message) ? err.message : 'An Error Occurred',
          errorDetails: 'Something went wrong while loading the Assessment page.'
        })
      })
    );
  } finally {
    NProgress.done();
  }
}

function showAuthError() {
  Scaffold.getRegion('content').show(
    new ErrorView({
      model: new Backbone.Model({
        errorMessage: 'Not Authorized',
        errorDetails: 'You are not yet an authorized user. Please contact an administrator.'
      })
    })
  );
  NProgress.done();
}
